import { Badge, Box, Button, Card, Grid, Group, Paper, Progress, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import {
  IconBrandShopee,
  IconChartBar,
  IconCheck,
  IconClick,
  IconColorSwatch,
  IconDeviceDesktop,
  IconMail,
  IconPlayerPlay,
  IconRocket,
  IconSettings,
  IconShoppingCart,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import Metadata from '../components/Metadata';
import Spinner from '../components/Spinner';
import { Page } from '../constants/pageTitles';
import useFetch from '../helpers/useFetch';
import useDriverTour from '../hooks/useDriverTour';
import { addUserAction } from '../store/authSlice';

const tabConfig = {
  gettingStarted: { icon: IconSettings, color: 'violet' },
  brandIdentity: { icon: IconColorSwatch, color: 'red' },
  exploreThePlatform: { icon: IconDeviceDesktop, color: 'blue' },
  firstCampaign: { icon: IconMail, color: 'teal' },
  activateWorkflows: { icon: IconClick, color: 'orange' },
  firstBanner: { icon: IconShoppingCart, color: 'pink' },
  firstRecommender: { icon: IconShoppingCart, color: 'cyan' },
  createSegment: { icon: IconUsersGroup, color: 'grape' },
  analytics: { icon: IconChartBar, color: 'indigo' },
};

const SetupGuidePage = ({ guide }) => {
  const page = `${Page['userGuide']}`;
  const { accountId, domainId } = useParams();
  const domain = useSelector((state) => state.domain.domain);
  const fetch = useFetch();
  const dispatch = useDispatch();

  const { startTour } = useDriverTour({
    onComplete: (taskId) => {
      const completedTask = { action: 'taskCompleted', context: { task: taskId } };
      fetch('/api/admin/userActions', {
        method: 'POST',
        body: JSON.stringify(completedTask),
      }).then(() => {
        dispatch(addUserAction(completedTask));
      });
    },
  });

  // Calculate overall progress
  const calculateProgress = () => {
    if (!guide) return { completed: 0, total: 0, percentage: 0 };
    const allTasks = guide.tabs.flatMap((tab) => tab.tasks);
    const completed = allTasks.filter((task) => task.isComplete).length;
    const total = allTasks.length;
    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  const progress = calculateProgress();

  return (
    <div>
      <Metadata title={page} />
      <Box my={10}>
        {/* Header Section */}
        <Paper
          p="xl"
          mb="xl"
          radius="md"
          data-tour="userGuideHeading"
          style={{
            background: 'linear-gradient(135deg, var(--mantine-color-violet-6) 0%, var(--mantine-color-grape-6) 100%)',
          }}
        >
          <Grid align="center">
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Group gap="md" mb="sm">
                <ThemeIcon size={50} radius="md" variant="white" color="violet">
                  <IconRocket size={28} />
                </ThemeIcon>
                <div>
                  <Title order={2} c="white">
                    {page}
                  </Title>
                  <Text c="white" opacity={0.9} size="lg">
                    Here are some steps to help you get started
                  </Text>
                </div>
              </Group>
              <Group gap="md" mt="md">
                <Button
                  component="a"
                  href={`#/accounts/${accountId}/domains/${domainId}/analytics?welcomeGuide=1`}
                  leftSection={<IconPlayerPlay size={16} />}
                  size="sm"
                  className="guide-header-btn"
                >
                  Welcome guide
                </Button>
                {domain.platform === 'shopify' && (
                  <Button
                    component="a"
                    href={`#/accounts/${accountId}/domains/${domainId}/shopifyIntegration`}
                    leftSection={<IconBrandShopee size={16} />}
                    size="sm"
                    className="guide-header-btn"
                  >
                    Shopify guide
                  </Button>
                )}
              </Group>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Paper p="md" radius="md" bg="white">
                <Text size="sm" fw={500} mb="xs">
                  Your Progress
                </Text>
                <Progress value={progress.percentage} size="lg" radius="xl" color="violet" mb="xs" />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    {progress.completed} of {progress.total} tasks completed
                  </Text>
                  <Badge color="violet" variant="light">
                    {progress.percentage}%
                  </Badge>
                </Group>
              </Paper>
            </Grid.Col>
          </Grid>
        </Paper>

        {!guide && <Spinner />}
        {guide && (
          <Grid ta="left">
            {guide.tabs
              .filter((tab) => tab.tasks.length)
              .map((tab, index) => {
                const config = tabConfig[tab.id] || { icon: IconSettings, color: 'gray' };
                const TabIcon = config.icon;
                const completedTasks = tab.tasks.filter((t) => t.isComplete).length;
                const tabProgress = tab.tasks.length > 0 ? Math.round((completedTasks / tab.tasks.length) * 100) : 0;

                return (
                  <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={index} pb="md">
                    <Card
                      withBorder
                      radius="md"
                      style={{
                        minHeight: '280px',
                        height: '100%',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      }}
                      styles={{
                        root: {
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 'var(--mantine-shadow-md)',
                          },
                        },
                      }}
                    >
                      <Card.Section
                        inheritPadding
                        py="md"
                        style={{
                          background: `linear-gradient(135deg, var(--mantine-color-${config.color}-0) 0%, var(--mantine-color-${config.color}-1) 100%)`,
                          borderBottom: `1px solid var(--mantine-color-${config.color}-2)`,
                        }}
                      >
                        <Group justify="space-between" align="flex-start">
                          <Group gap="sm">
                            <ThemeIcon size={40} radius="md" color={config.color} variant="light">
                              <TabIcon size={22} />
                            </ThemeIcon>
                            <div>
                              <Text fw={600} size="md">
                                {tab.title}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {completedTasks}/{tab.tasks.length} completed
                              </Text>
                            </div>
                          </Group>
                          {tabProgress === 100 && (
                            <Badge color="green" variant="filled" size="sm">
                              Done
                            </Badge>
                          )}
                        </Group>
                        <Progress value={tabProgress} size="xs" radius="xl" color={config.color} mt="sm" />
                      </Card.Section>
                      <Card.Section inheritPadding py="md">
                        <Stack gap="xs">
                          {tab.tasks.map((task, taskIndex) => {
                            return (
                              <Box key={taskIndex}>
                                <Button
                                  variant={task.isComplete ? 'light' : 'subtle'}
                                  color={task.isComplete ? 'green' : config.color}
                                  justify="flex-start"
                                  fullWidth
                                  styles={{
                                    root: {
                                      height: 'auto',
                                      padding: '8px 12px',
                                    },
                                    label: {
                                      whiteSpace: 'normal',
                                      textAlign: 'left',
                                    },
                                  }}
                                  onClick={() => {
                                    // A task that is a page of its own opens it instead of a tour.
                                    if (task.onClick) {
                                      task.onClick();
                                    } else {
                                      startTour(task.steps, { taskId: task.id });
                                    }
                                  }}
                                  leftSection={
                                    task.isComplete ? (
                                      <ThemeIcon size={20} radius="xl" color="green" variant="filled">
                                        <IconCheck size={12} />
                                      </ThemeIcon>
                                    ) : (
                                      <ThemeIcon size={20} radius="xl" color={config.color} variant="light">
                                        <Text size="xs" fw={600}>
                                          {taskIndex + 1}
                                        </Text>
                                      </ThemeIcon>
                                    )
                                  }
                                >
                                  {task.title}
                                </Button>
                                {task.note && (
                                  <Text size="xs" c="dimmed" px="sm" pt={4}>
                                    {task.note}
                                  </Text>
                                )}
                              </Box>
                            );
                          })}
                        </Stack>
                      </Card.Section>
                    </Card>
                  </Grid.Col>
                );
              })}
          </Grid>
        )}
      </Box>
    </div>
  );
};

export default SetupGuidePage;
